import type { RouteRecordRaw } from 'vue-router';
import { useStoreStore } from '@/stores/store-store';
import { LANDING_ROUTE_NAME, resolveLandingTarget } from './landing';
import { ENABLE_ADD_STORE, ENABLE_PAID_PLANS } from '@/consts';
import { isRouteVisible } from '@/market';

// Layout thuộc về NHÓM route, nên nó được khai ở đây — đúng một lần mỗi layout. Mỗi màn trong
// `pages/<slug>/route.ts` chỉ còn là một record con: name + path đầy đủ + component + meta.
//
// `path` của record con là path đầy đủ TRỪ dấu `/` đầu (`'setup/store'`, `'user/profile'`), vì
// cả ba nhóm cùng treo dưới gốc `'/'`. Meta ở lại từng màn: `meta.i18n` là `import.meta.glob`
// mà Vite phân giải theo file chứa nó, mang lên đây là trỏ nhầm thư mục.
import {
  giftCardRoutes,
  promotionManagementRoutes,
  bannerManagementRoutes,
  deviceManagementRoutes,
  billingDetailRoutes,
  employeeCheckinRoutes,
  storeRoutes,
  signinRoutes,
  signupRoutes,
  forgotPasswordRoutes,
  forgotPasswordSentRoutes,
  frontDeskCheckoutRoutes,
  frontDeskRoutes,
  resetPasswordRoutes,
  resetPasswordSuccessRoutes,
  resetPasswordInvalidRoutes,
  setPasswordRoutes,
  confirmEmailRoutes,
  bannedRoutes,
  storePickerRoutes,
  customerReviewRoutes,
  reviewsRoutes,
  serviceManagementRoutes,
  turnTrackerRoutes,
  customerManagementRoutes,
  promotionBannerRoutes,
  userRoutes,
  bookAppointmentRoutes,
  createAppointmentRoutes,
  customerCheckinRoutes,
  customerCheckinListRoutes,
  setupRoutes,
  setupStoreRoutes,
  setupPaymentMethodRoutes,
  staffsRoutes,
  setupCheckoutRoutes,
  setupTurnTrackerRoutes,
  setupStoreLandingRoutes,
  pricingRoutes,
  pricingFreeRoutes,
  onboardingRoutes,
  spinCampaignsRoutes,
  createSpinCampaignRoutes,
  editSpinCampaignRoutes,
  spinCampaignDetailRoutes,
  spinCustomerRoutes,
  reportRoutes,
  notFoundRoutes,
} from '@/pages';

/** Màn sau đăng nhập: nav trái + header. */
const mainLayoutChildren: RouteRecordRaw[] = [
  giftCardRoutes,
  promotionManagementRoutes,
  bannerManagementRoutes,
  deviceManagementRoutes,
  // Gói trả phí tắt tới 01/01/2027 → Billing Detail không có gì để hiện (nó chỉ có
  // hoá đơn + nút Upgrade mở bảng 3 gói). Không đăng ký route thay vì chỉ ẩn link:
  // cả hai screen này vốn KHÔNG có lối vào trong nav, chỉ vào được bằng URL. Redirect
  // của nó tắt CÙNG chỗ dưới `routes` — trỏ tới route name này nên bật lệch một bên là
  // `router.push` ném MATCHER_NOT_FOUND, y hệt cặp Add Store ở trên.
  ...(ENABLE_PAID_PLANS ? [billingDetailRoutes] : []),
  employeeCheckinRoutes,
  ...(ENABLE_ADD_STORE ? [storeRoutes] : []),
  customerReviewRoutes,
  reviewsRoutes,
  serviceManagementRoutes,
  turnTrackerRoutes,
  frontDeskRoutes,
  frontDeskCheckoutRoutes,
  customerManagementRoutes,
  promotionBannerRoutes,
  userRoutes,
  bookAppointmentRoutes,
  createAppointmentRoutes,
  customerCheckinRoutes,
  customerCheckinListRoutes,
  setupRoutes,
  setupStoreRoutes,
  setupPaymentMethodRoutes,
  staffsRoutes,
  setupCheckoutRoutes,
  setupTurnTrackerRoutes,
  setupStoreLandingRoutes,
  // `/pricing` do screen Free phục vụ khi chưa thu phí; bảng 3 gói giữ nguyên code,
  // chỉ thôi đăng ký. Hai screen cùng path nên KHÔNG được bật đồng thời.
  ...(ENABLE_PAID_PLANS ? [pricingRoutes] : [pricingFreeRoutes]),
  spinCampaignsRoutes,
  createSpinCampaignRoutes,
  editSpinCampaignRoutes,
  spinCampaignDetailRoutes,
  spinCustomerRoutes,
  reportRoutes,
];

// Nhóm này là "layout auth", KHÔNG phải "công khai": `set-password` và `store-picker` vẫn mang
// `requireAuth`. Cả 11 màn đều dùng named view — `AuthLayout.vue` có <router-view name="brand">.
const authLayoutChildren: RouteRecordRaw[] = [
  signinRoutes,
  signupRoutes,
  forgotPasswordRoutes,
  forgotPasswordSentRoutes,
  resetPasswordRoutes,
  resetPasswordSuccessRoutes,
  resetPasswordInvalidRoutes,
  setPasswordRoutes,
  confirmEmailRoutes,
  bannedRoutes,
  storePickerRoutes,
];

/** Toàn màn hình, không chrome. */
const blankLayoutChildren: RouteRecordRaw[] = [onboardingRoutes];

/**
 * Loader của MainLayout — export để `initial-entry.ts` nhận ra "đích nằm dưới MainLayout" bằng chính
 * component của record (vue-router giữ nguyên tham chiếu trong `matched[0].components.default`).
 */
export const loadMainLayout = () => import('@/layouts/MainLayout.vue');

const routes: RouteRecordRaw[] = [
  // ĐIỂM VÀO CỦA APP, và là nơi DUY NHẤT biết `homeRoute`. Mọi chỗ khác trong FE muốn "về nhà"
  // thì chỉ việc đi tới `/` — không nơi nào khác được đọc `homeRoute` hay giữ bản sao của màn
  // mặc định.
  //
  // `redirect` dạng hàm chứ không phải guard: nó chạy lúc khớp bảng, đồng bộ, không cần component
  // — nên không có màn trắng, không timeout, không race. Đổi lại, `homeRoute` chỉ có tác dụng khi
  // store đã nạp xong (`App.vue` onMounted), tức từ lần điều hướng thứ hai. Chấp nhận được: đây là
  // cấu hình hiếm khi đổi, và mọi cách làm cho nó đúng ngay lần đầu đều đắt hơn nhiều (xem TASK-135).
  {
    path: '/',
    name: LANDING_ROUTE_NAME,
    redirect: () => resolveLandingTarget(useStoreStore().homeRoute),
  },
  // Ba nhóm layout dưới đây cũng khai `path: '/'`, trùng với record `landing` ở trên. Vue-router
  // xếp chúng NGANG HẠNG nên thứ tự khai báo là thứ quyết định: `landing` phải đứng trước ba
  // nhóm layout, nếu không `/` dựng một MainLayout rỗng và `homeRoute` mất tác
  // dụng hoàn toàn — im lặng, vì snapshot hợp đồng URL không phân biệt được hai thế giới đó.
  // `requireAuth` khai MỘT lần ở layout — vue-router gộp meta từ cha xuống con (`mergeMetaFields`),
  // nên mọi màn con đều mang nó trong `to.meta`. Không cờ/guard tiệm nào: đích "có tiệm chưa" quyết
  // lúc init, áp vào URL khởi đầu (`initial-entry.ts`); vào MainLayout từ ngoài sau đó đều reload.
  {
    path: '/',
    component: loadMainLayout,
    meta: { requireAuth: true },
    // `VITE_MARKET=au` thôi đăng ký các màn bị cắt (src/market) — code màn giữ nguyên.
    children: mainLayoutChildren.filter(route => isRouteVisible(route.name)),
  },
  { path: '/', component: () => import('@/layouts/AuthLayout.vue'), children: authLayoutChildren },
  { path: '/', component: () => import('@/layouts/blank.vue'), children: blankLayoutChildren },

  // Catch-all mang layout riêng và phải ở cuối — nó nuốt mọi path chưa khớp.
  notFoundRoutes,
];

export default routes;
